import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { useToastStore } from '@/stores/utils/toast.store';
import { Pagination } from '@/types/api/response.types';
import {
  BulkUpload,
  BulkUploadResponse,
  LocationParams,
  Vendor,
  VendorBody,
  VendorItem,
  VendorItemParams,
  VendorParams,
  VendorSync,
  VendorCityRefreshContext,
  VendorSource,
} from '@/types/api/vendors.types';
import {
  bulkUploadItems,
  createVendor,
  deleteImage,
  deleteVendor,
  getSyncedOrganisationCount,
  getVendorCities,
  getVendorItems,
  getVendors,
  getVendorSuburbs,
  syncVendors,
  updateVendor,
  uploadImage,
} from '@/api/vendors.service';
import { ProjectImageBody } from '@/types/api/project.types';
import { VendorType } from '@/common/constants';
import { useAuthStore } from '@/stores/auth.store';
import { Places } from '@/types/api/cities.types';
import { documentUploadToast } from '@/common/utils/general.utils';

/**
 * How many ids one `getVendorsByIds` request carries. Bounded because the ids ride in the query
 * string, which has a length limit no client controls.
 */
const VENDOR_ID_BATCH_SIZE = 50;

/** Page size for an id-filtered fetch. Paged through regardless, in case the server caps it. */
const VENDOR_PAGE_SIZE = 100;
import { analyticsService } from '@/plugins/post-hog';
import { selectedOrgId } from '@/common/utils/role.utils';

const { t } = i18n.global;
const currentOrgId = () => useAuthStore().loggedInUser.selectedOrg!.id;

export const useVendorStore = defineStore('vendors', {
  state: () => ({
    vendor: {} as Vendor,
    BOQVendors: [] as Vendor[],
    allSelectedBOQVendors: [] as Vendor[],
    BOQSyncedVendorIds: [] as string[],
    unsyncedVendorIds: [] as string[],
    paginatedVendors: [] as Vendor[],
    paginatedVendorItems: [] as VendorItem[],
    bulkUploadInvalidFile: false,
    bulkUploadErrors: {} as BulkUploadResponse,
    orgCities: [] as Places[],
    cities: [] as Places[],
    suburbs: [] as Places[],
    vendorForLogPayment: {} as Vendor,

    pagination: {} as Pagination,
    vendorItemsPagination: {} as Pagination,
    BOQPagination: {} as Pagination,

    vendorParams: {
      pageNo: 1,
      pageSize: 10,
      orderBy: 'name',
    } as VendorParams,

    vendorItemParams: {
      pageNo: 1,
      pageSize: 15,
    } as VendorItemParams,
  }),

  actions: {
    async createVendor(data: VendorBody, customItem?: boolean) {
      const toastStore = useToastStore();
      const response = await createVendor(data);
      this.vendor = response.data.data;

      if (response.status === 200 && !customItem) {
        toastStore.showToast(
          'success',
          t('vendors.createVendorSuccess.title'),
          t('vendors.createVendorSuccess.content'),
        );
      }

      const refreshContext: VendorCityRefreshContext =
        data.source === VendorSource.custom ? 'org-cities' : 'db-cities';
      await this.refreshVendorCitiesAfterWrite(refreshContext);

      return response;
    },

    /**
     * `isCurrent` lets a caller whose requests can overlap keep only the latest: it runs once the
     * response arrives, and a stale one commits nothing.
     */
    async getVendors(
      params?: VendorParams,
      logPayment = false,
      options?: { isCurrent?: () => boolean },
    ) {
      const response = await getVendors({ ...this.vendorParams, ...params });

      if (options?.isCurrent && !options.isCurrent()) return;

      const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
      this.pagination = { pageCount, totalCount, pageNo, pageSize };

      if (logPayment) {
        this.vendorForLogPayment = response.data.data[0] ?? ({} as Vendor);
        return;
      }

      if (params?.ids && params.ids.length > 0) {
        this.mergeVendors(response.data.data);
      } else {
        this.paginatedVendors = response.data.data;
      }
    },

    /** Adds vendors to the cached list, replacing any already held, without disturbing the rest. */
    mergeVendors(vendors: Vendor[]) {
      for (const vendor of vendors) {
        const index = this.paginatedVendors.findIndex((existing) => existing.id === vendor.id);

        if (index === -1) {
          this.paginatedVendors.push(vendor);
        } else {
          this.paginatedVendors[index] = vendor;
        }
      }
    },

    /** Reads one id-filtered query to the end of its result set, merging every page. */
    async fetchVendorPage(params: VendorParams) {
      let pageNo = 1;
      let pageCount = 1;
      const fetched: string[] = [];

      do {
        const response = await getVendors({ ...params, pageNo, pageSize: VENDOR_PAGE_SIZE });

        this.mergeVendors(response.data.data);
        fetched.push(...response.data.data.map((vendor: Vendor) => vendor.id));
        pageCount = response.data.meta.pagination.pageCount || 1;
        pageNo += 1;
      } while (pageNo <= pageCount);

      // The ids this call actually returned. `getVendorsByIds` needs them to decide what to ask the
      // other population for — the cached list can't answer that (see the note there).
      return fetched;
    },

    /**
     * Fetches specific vendors by id and merges them into the cached list.
     *
     * Use this rather than `getVendors({ ids })` whenever *every* requested vendor has to arrive,
     * because a plain ids-fetch misses two ways and both are silent.
     *
     * Context: `organisationId` is not a narrowing filter on this endpoint, it selects which of
     * two populations is searched. Passing it returns only that org's own vendors; omitting it
     * returns only the central BOQPAY catalogue, whose vendors carry no organisation. No single
     * request spans both — so an id belonging to the other population comes back as nothing,
     * which is indistinguishable from a vendor that doesn't exist. A BOQ line can reference
     * either, so resolving ids means asking both, second only for whatever the first didn't find.
     *
     * The ids also go out in fixed batches, each read to the end of its own result set, so no
     * page boundary can drop a vendor either.
     */
    async getVendorsByIds(ids: string[], organisationId: string) {
      const unique = [...new Set(ids.filter(Boolean))];

      for (let start = 0; start < unique.length; start += VENDOR_ID_BATCH_SIZE) {
        const batch = unique.slice(start, start + VENDOR_ID_BATCH_SIZE);

        // What the org population returned, not what the cache happens to hold. Judging by the cache
        // let a *stale* copy of a central vendor stand in for a fetch: the org call can't return it,
        // the cached copy made it look resolved, and the central call was skipped — so the flow
        // priced and paid against whatever that copy said, however old.
        const resolved = new Set(await this.fetchVendorPage({ organisationId, ids: batch }));
        const central = batch.filter((id) => !resolved.has(id));

        if (central.length > 0) await this.fetchVendorPage({ ids: central });
      }
    },

    async getBOQVendors(params?: VendorParams, selectAll?: boolean) {
      await this.getBOQSyncedVendors();

      const response = await getVendors({ ...params });

      if (selectAll) {
        this.allSelectedBOQVendors = response.data.data;

        this.BOQSyncedVendorIds.forEach((id: string) => {
          this.allSelectedBOQVendors.forEach((vendor: Vendor) => {
            if (vendor.id === id) {
              vendor.BOQSynced = true;
            }
          });
        });
      } else {
        const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
        this.BOQPagination = { pageCount, totalCount, pageNo, pageSize };

        this.BOQVendors = response.data.data;

        this.BOQSyncedVendorIds.forEach((id: string) => {
          this.BOQVendors.forEach((vendor: Vendor) => {
            if (vendor.id === id) {
              vendor.BOQSynced = true;
            }
          });
        });
      }
    },

    async uploadImage(data: ProjectImageBody, options?: { silent?: boolean }) {
      const response = await uploadImage(data);

      documentUploadToast({
        responseCode: response.status,
        title: t('vendors.imageUploadSuccess.title'),
        description: t('vendors.imageUploadSuccess.content'),
        showToast: !options?.silent,
        onSuccess: async () => {
          this.vendor = response.data.data;
        },
      });
    },

    async deleteImage(id: string, options?: { silent?: boolean }) {
      const toastStore = useToastStore();

      const response = await deleteImage(id);

      if (response.status === 204 && !options?.silent) {
        toastStore.showToast(
          'success',
          t('vendors.imageRemoveSuccess.title'),
          t('vendors.imageRemoveSuccess.content'),
        );
      }
    },

    async updateVendor(id: string, data: VendorBody, isDbVendor: boolean) {
      const toastStore = useToastStore();

      const response = await updateVendor(id, data);

      if (response.status === 200) {
        this.vendor = response.data.data;
        toastStore.showToast(
          'success',
          t('vendors.updateVendorSuccess.title'),
          t('vendors.updateVendorSuccess.content'),
        );

        // Each list page loads its own cities when it opens, so only the page saved from needs
        // refreshing; the catalog admin may not read the org's cities at all (PLE-1775).
        await this.refreshVendorCities(isDbVendor ? 'db-cities' : 'org-cities');
      }
    },

    /**
     * Context: asked fresh on every central Save and never kept, because the count is only true as
     * at the moment it is asked, and a held one would warn with a reach that has since changed.
     */
    async getSyncedOrganisationCount(vendorId: string): Promise<number> {
      const response = await getSyncedOrganisationCount(vendorId);
      return response.data.data.count;
    },

    async syncVendors(data: VendorSync) {
      await syncVendors(data);

      analyticsService.capture('org_vendors_synced', {
        org_id: selectedOrgId.value!,
      });

      await this.refreshVendorCitiesAfterWrite('both');
    },

    /**
     * Context: a sync sends the organisation's whole synced set and the API keeps only that set, so
     * a vendor missing from this list is unsynced (with its items) on the next sync. Every page has
     * to be read; one page of 200 dropped the rest for a larger organisation (PLE-1632).
     */
    async getBOQSyncedVendors() {
      const synced: Vendor[] = [];
      let pageNo = 1;
      let pageCount = 1;

      do {
        const response = await getVendors({
          pageNo,
          pageSize: VENDOR_PAGE_SIZE,
          source: VendorType.boq.key,
          organisationId: currentOrgId(),
        });

        synced.push(...response.data.data);
        pageCount = response.data.meta.pagination.pageCount || 1;
        pageNo += 1;
      } while (pageNo <= pageCount);

      // Only a synced vendor has a parent to point at; the rest are the org's own.
      this.BOQSyncedVendorIds = synced
        .map((vendor) => vendor.parentId)
        .filter((id): id is string => id !== null);
    },

    async getVendorItems(id: string, params?: VendorItemParams) {
      const response = await getVendorItems(id, { ...this.vendorItemParams, ...params });

      const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
      this.vendorItemsPagination = { pageCount, totalCount, pageNo, pageSize };

      this.paginatedVendorItems = response.data.data;
    },

    async bulkUploadItems(data: BulkUpload, staging: boolean) {
      const toastStore = useToastStore();
      const response = await bulkUploadItems(data);

      this.bulkUploadErrors = response.data.data;

      if (!staging) {
        toastStore.showToast(
          'success',
          t('vendors.bulkUploadItems.successfulUpload.title'),
          t('vendors.bulkUploadItems.successfulUpload.content'),
        );

        analyticsService.capture('bulk_items_uploaded', {
          org_id: selectedOrgId.value!,
          vendor_id: data.id,
        });
      }
    },

    async deleteVendor(id: string, refreshContext?: VendorCityRefreshContext) {
      const toastStore = useToastStore();

      const vendorToDelete = this.paginatedVendors.find((vendor) => vendor.id === id);

      const response = await deleteVendor(id);

      if (response.status === 204) {
        toastStore.showToast(
          'success',
          t('vendors.removeVendorSuccess.title'),
          t('vendors.removeVendorSuccess.content'),
        );

        this.paginatedVendors = this.paginatedVendors.filter((vendor) => vendor.id !== id);

        const context: VendorCityRefreshContext =
          refreshContext ?? (vendorToDelete?.organisationId ? 'org-cities' : 'db-cities');

        await this.refreshVendorCities(context);
      }
    },

    /**
     * Refresh vendor cities based on context
     * @param context - Which cities to refresh:
     *                  'org-cities': Refresh only organisation-specific cities
     *                  'db-cities': Refresh only global database cities
     *                  'both': Refresh both org and db cities
     */
    /**
     * Context: the city lists are a projection read after a vendor write. Once the write has
     * succeeded, a failed read must not make the action reject, or the caller treats a created or
     * synced vendor as not saved and repeats the write. The interceptor still reports the failure.
     */
    async refreshVendorCitiesAfterWrite(context: VendorCityRefreshContext) {
      try {
        await this.refreshVendorCities(context);
      } catch {
        // Stale cities until the next refresh; the vendor write itself stands.
      }
    },

    async refreshVendorCities(context: VendorCityRefreshContext = 'both') {
      switch (context) {
        case 'org-cities': {
          await this.getVendorCities({ orgId: currentOrgId() });
          break;
        }

        case 'db-cities': {
          await this.getVendorCities();
          break;
        }

        case 'both': {
          await Promise.all([
            this.getVendorCities({ orgId: currentOrgId() }),
            this.getVendorCities(),
          ]);
          break;
        }
      }
    },

    async getVendorCities(params?: LocationParams) {
      const response = await getVendorCities({ ...params });

      const cityNames = response.data.data;

      if (params?.orgId) {
        this.orgCities = cityNames.map((city) => ({ name: city, value: city }));
      } else {
        this.cities = cityNames.map((city) => ({ name: city, value: city }));
      }
    },

    async getVendorSuburbs(params?: LocationParams) {
      const response = await getVendorSuburbs({ ...params });

      const suburbNames = response.data.data;

      this.suburbs = suburbNames.map((suburb) => ({ name: suburb, value: suburb }));
    },
  },
});
